import React, { useState, useCallback, createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import 'styles/Message.scss';

const MessageContext = createContext(null);

export const useMessage = () => {
    const context = useContext(MessageContext);
    if (!context) {
        throw new Error('useMessage must be used within a MessageProvider');
    }
    return context;
};

const MessageItem = ({ type, content, onClose }) => {
    const iconClassMap = {
        error: 'icon-error-small',
        warning: 'icon-warning-small',
        success: 'icon-information-small',
        info: 'icon-information-small',
    };
    const defaultIconClass = iconClassMap[type] || iconClassMap.success;

    React.useEffect(() => {
        const timer = setTimeout(() => {
            onClose();

        }, 3000);

        return () => clearTimeout(timer);
    }, [onClose]);

    const classes = classNames('toast-notification', {
        [`toast-${type}`]: type,
    });

    return (
        <div className={classes}>
            <div className="toast-icon">
                <i className={defaultIconClass} />
            </div>
            <div className="toast-info">
                <div className="toast-content">{content}</div>
            </div>
        </div>
    );
};

MessageItem.propTypes = {
    type: PropTypes.oneOf(['success', 'error', 'warning', 'info']).isRequired,
    content: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
    onClose: PropTypes.func.isRequired,
};

export const MessageProvider = ({ children }) => {
    const [messages, setMessages] = useState([]);

    const addMessage = useCallback((type, content) => {
        const id = Date.now();
        setMessages(prev => [...prev, { id, type, content }]);
    }, []);

    const removeMessage = useCallback((id) => {
        setMessages(prev => prev.filter(msg => msg.id !== id));
    }, []);

    const messageApi = React.useMemo(() => ({
        success: (content) => addMessage('success', content),
        error: (content) => addMessage('error', content),
        warning: (content) => addMessage('warning', content),
        info: (content) => addMessage('info', content),
    }), [addMessage]);

    return (
        <MessageContext.Provider value={messageApi}>
            {children}
            <div className="toast-container">
                {messages.map(msg => (
                    <div key={msg.id} className="toast-item">
                        <MessageItem
                            type={msg.type}
                            content={msg.content}
                            onClose={() => removeMessage(msg.id)}
                        />
                    </div>
                ))}
            </div>
        </MessageContext.Provider>
    );
};

MessageProvider.propTypes = {
    children: PropTypes.node,
};